<!--
  FavouritesSidebar — contextual sidebar contents for the "Favourites"
  cauldron.

  Lists ALL the user's favourites (no cap), most-recently-favourited first.
  Each row routes directly to that favourite's `to` target. When the user
  has no favourites yet, shows an empty-state hint instead of an empty
  list so the cauldron explains itself.
-->
<template>
  <div class="flex flex-col h-full">
    <nav class="flex-1 overflow-y-auto px-3 py-3" aria-label="Favourites navigation">
      <div v-if="!collapsed" class="sidebar-section-label">
        {{ t('cauldron.favourites.label') }}
      </div>

      <!-- Empty state — surfaces the affordance ("click the star on any page")
           so a fresh user understands why the cauldron exists. -->
      <div
        v-if="sortedFavourites.length === 0"
        class="px-2.5 py-4 text-xs text-theme-text-secondary space-y-1"
      >
        <p class="font-medium text-theme-text-secondary">
          {{ t('cauldron.favourites.empty_title') }}
        </p>
        <p>{{ t('cauldron.favourites.empty_message') }}</p>
      </div>

      <!-- Favourites — uncapped. Long lists scroll inside the cauldron's
           own pane; the surrounding shell stays steady. -->
      <sidebar-menu-item
        v-for="fav in sortedFavourites"
        :key="fav.id"
        :to="fav.to"
        :label="fav.label"
        :icon="fav.icon || mdiStar"
        :title="fav.label"
        :collapsed="collapsed"
        size="sm"
        inactive-icon-class="text-theme-palette-amber dark:text-theme-palette-amber"
        @click="emit('navigate')"
      />
    </nav>
  </div>
</template>

<script setup>
  /* eslint-disable no-undef */
  import SidebarMenuItem from '@/components/navigation/SidebarMenuItem.vue';
  import { loadFavorites, useFavorites } from '@/composables/favorites';
  import { mdiStar } from '@mdi/js';
  import { computed, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useAuthStore } from '@/stores/useAuthStore';

  defineProps({
    collapsed: { type: Boolean, default: false },
  });
  const emit = defineEmits(['navigate']);

  const { t } = useI18n();
  const authStore = useAuthStore();
  const isAuthenticated = computed(() => authStore.isAuthenticated);

  // No cap — the cauldron is a personal index of every page the user has
  // starred, so we list them all. Most-recently-favourited first.
  const { favorites } = useFavorites();
  const sortedFavourites = computed(() => {
    const arr = (favorites.value || []).filter((f) => f && f.to && f.label);
    arr.sort((a, b) => {
      const ta = a.favorited_at ? new Date(a.favorited_at).getTime() : 0;
      const tb = b.favorited_at ? new Date(b.favorited_at).getTime() : 0;
      return tb - ta;
    });
    return arr;
  });

  onMounted(() => {
    if (isAuthenticated.value && (!favorites.value || favorites.value.length === 0)) {
      loadFavorites();
    }
  });
</script>
